import React, { useState } from 'react';
import { X } from 'lucide-react';
import { createBoard } from '../lib/boards';
import { UserTypeInfo } from '../types/board';

interface AddBoardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBoardCreated: () => void;
  businessId: string;
  userInfo: UserTypeInfo;
}

export const AddBoardModal: React.FC<AddBoardModalProps> = ({
  isOpen,
  onClose,
  onBoardCreated,
  businessId,
  userInfo
}) => {
  const [boardTitle, setBoardTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!boardTitle.trim()) {
      setError('Board title is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('AddBoardModal: Creating board with:', {
        businessId,
        boardTitle: boardTitle.trim(),
        userInfo
      });
      
      await createBoard(businessId, boardTitle.trim(), userInfo);
      
      // Reset form
      setBoardTitle('');
      setError(null);
      
      // Close modal and refresh boards
      onClose();
      onBoardCreated();
    } catch (err) {
      console.error('AddBoardModal: Error creating board:', err);
      setError(err instanceof Error ? err.message : 'Failed to create board');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setBoardTitle('');
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Create New Board</h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label htmlFor="boardTitle" className="block text-sm font-medium text-gray-700 mb-2">
              Board Title
            </label>
            <input
              type="text"
              id="boardTitle"
              value={boardTitle}
              onChange={(e) => setBoardTitle(e.target.value)}
              placeholder="Enter board title..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
              maxLength={100}
            />
          </div>

          {/* User Info Display */}
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <div className="text-sm text-gray-600">
              <div className="font-medium text-gray-900 mb-1">Assigned to:</div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  userInfo.userType === 'admin' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {userInfo.userType === 'admin' ? 'Admin' : 'Resource'}
                </span>
                <span className="text-gray-700">
                  {userInfo.userType === 'admin' ? 'Admin' : userInfo.resourceName || 'Resource'}
                </span>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !boardTitle.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating...' : 'Create Board'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 